import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GetProfileResponse, getProfile } from "../api";

/** Everything that reads `/me` shares this key, so one request serves them all. */
export const ME_QUERY_KEY = ["me"] as const;

/** How often the signed-in session is re-validated against the server. */
export const SESSION_POLL_MS = 10_000;

/*
  `/me` is the session check and the profile at once — the two used to be
  separate calls to the same endpoint. React Query dedupes callers, so a page
  that needs the balance and a provider that needs the identity cost one
  request between them, and StrictMode's double-mount does not double it.

  Structural sharing matters as much as the dedupe here: a poll that comes back
  unchanged keeps the previous object, so `user` keeps its identity and the
  effects keyed on it do not re-run every ten seconds.
*/
export const useProfileQuery = (enabled: boolean) =>
  useQuery<GetProfileResponse>({
    queryKey: ME_QUERY_KEY,
    queryFn: getProfile,
    enabled,
    refetchInterval: enabled ? SESSION_POLL_MS : false,
    // A rejected /me means the session is gone; retrying cannot revive it.
    retry: false,
    staleTime: SESSION_POLL_MS,
  });

export const useInvalidateProfile = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
};
